interface Api {
  url: string;
}

interface Auth {
  clientId: string;
  authority: string;
  apiScope: string;
}

export type LogLevel = 'verbose' | 'debug' | 'information' | 'warning' | 'error' | 'fatal';
export type TelemetrySink = 'console' | 'appInsights';

interface AppInsights {
  // Not a secret: shipped to the browser. Empty disables telemetry. Get it via `tofu output -raw app_insights_client_connection_string` in infra/.
  connectionString: string;
  minLogLevel: LogLevel;
  sink: TelemetrySink;
}

interface Env {
  name: string;
  production: boolean;
  api: Api;
  auth: Auth;
  appInsights: AppInsights;
  serviceWorker: boolean;
  offlineRecoveryRetryIntervalMs?: number;
}

type EnvName = 'local' | 'dev' | 'test' | 'prod';
//26b749f6-8c72-44e3-bbde-ae3de07b4206
const envData = {
  default: {
    production: false,
    auth: {
      clientId: '386fac73-518a-4d41-9a9e-667ece6fdfdb',
      authority: 'https://login.microsoftonline.com/26b749f6-8c72-44e3-bbde-ae3de07b4206',
      apiScope: 'api://8d152336-8973-42eb-9219-a6fc2a138b32/api.access',
    },
    offlineRecoveryRetryIntervalMs: 10_000,
    name: 'Local',
    appInsights: {
      connectionString: '',
      minLogLevel: 'debug' as LogLevel,
      sink: 'console' as TelemetrySink,
    },
  },
  envSpecificData: {
    local: {
      api: {
        url: 'http://localhost:8080/api',
      },
      serviceWorker: true,
    },
    dev: {
      api: {
        url: 'https://dev.dwo.akersolutions.com/api',
      },
      serviceWorker: true,
      name: 'dev',
      appInsights: {
        connectionString:
          'InstrumentationKey=8d698a5b-e63c-42d1-85e1-ef4660a308da;IngestionEndpoint=https://westeurope-5.in.applicationinsights.azure.com/;LiveEndpoint=https://westeurope.livediagnostics.monitor.azure.com/;ApplicationId=cd286ab3-0e57-4a40-ae1c-a680b7ebabc9',
        minLogLevel: 'debug' as LogLevel,
        sink: 'appInsights' as TelemetrySink,
      },
    },
    test: {
      api: {
        url: 'https://test.dwo.akersolutions.com/api',
      },
      production: true,
      serviceWorker: true,
      name: 'test',
      appInsights: {
        connectionString:
          'InstrumentationKey=0fdc1f91-6f6d-4751-ac70-8e5c7642ebe3;IngestionEndpoint=https://westeurope-5.in.applicationinsights.azure.com/;LiveEndpoint=https://westeurope.livediagnostics.monitor.azure.com/;ApplicationId=1836512a-62be-4d3f-b9ba-613b86eab43f',
        minLogLevel: 'information' as LogLevel,
        sink: 'appInsights' as TelemetrySink,
      },
    },
    prod: {
      api: {
        url: 'https://dwo.akersolutions.com/api',
      },
      production: true,
      auth: {
        clientId: 'f28f204c-7ee3-467a-951e-5be5afd9a99a',
        authority: 'https://login.microsoftonline.com/26b749f6-8c72-44e3-bbde-ae3de07b4206',
        apiScope: 'api://bba5bad6-44fb-40e8-a98d-19b3c20501d6/api.access',
      },
      serviceWorker: true,
      name: 'prod',
      appInsights: {
        connectionString:
          'InstrumentationKey=475f92f7-d0b7-4861-ba8d-b666fd3a8904;IngestionEndpoint=https://westeurope-5.in.applicationinsights.azure.com/;LiveEndpoint=https://westeurope.livediagnostics.monitor.azure.com/;ApplicationId=a2f4e5a2-a829-479a-aace-ef4f5ab67265',
        minLogLevel: 'information' as LogLevel,
        sink: 'appInsights' as TelemetrySink,
      },
    },
  },
  getWithSpecificData(envName: EnvName): Env {
    return {
      ...this.default,
      ...this.envSpecificData[envName],
    };
  },
  get(): Env {
    const hostname = window.location.hostname;

    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      return this.getWithSpecificData('local');
    }

    if (hostname.includes('dev')) {
      return this.getWithSpecificData('dev');
    }

    if (hostname.includes('test')) {
      return this.getWithSpecificData('test');
    }

    return this.getWithSpecificData('prod');
  },
};

export const env = (() => {
  const instance = envData.get();
  return {
    get: () => instance,
  };
})();
